/**
 * Keeps the country/currency toggle label and list highlight aligned with what
 * the customer is actually shopping in. Liquid and Shopify.currency.active can
 * disagree with cart/session currency (common on primary markets like Canada).
 *
 * Strategy (in order):
 * 1. sessionStorage — set when the user picks a currency (survives reload; no console needed)
 * 2. ?currency= URL param
 * 3. window.Shopify.currency.active
 * 4. GET /cart.js — cart.currency is the storefront source of truth for the session
 */

const STORAGE_KEY = "rebalance_display_currency";

function isIso4217(code: string): boolean {
  return /^[A-Za-z]{3}$/.test(code);
}

export function persistDisplayCurrencyPreference(isoCode: string): void {
  const trimmed = isoCode.trim();
  if (!isIso4217(trimmed)) {
    return;
  }
  try {
    window.sessionStorage.setItem(STORAGE_KEY, trimmed.toUpperCase());
  } catch {
    // Quota or private mode — cart.js sync still runs.
  }
}

function readStoredDisplayCurrency(): string | null {
  try {
    const v = window.sessionStorage.getItem(STORAGE_KEY)?.trim();
    if (v && isIso4217(v)) {
      return v.toUpperCase();
    }
  } catch {
    // ignore
  }
  return null;
}

function getCartJsUrl(): string {
  const root = window.Shopify?.routes?.root;
  if (typeof root === "string" && root.length > 0) {
    return root.endsWith("/") ? `${root}cart.js` : `${root}/cart.js`;
  }
  return "/cart.js";
}

function getActiveCurrencyCodeSync(): string | null {
  const stored = readStoredDisplayCurrency();
  if (stored) {
    return stored;
  }

  try {
    const fromUrl = new URL(window.location.href).searchParams.get("currency");
    if (fromUrl && isIso4217(fromUrl)) {
      return fromUrl.toUpperCase();
    }
  } catch {
    // ignore
  }

  const shopify = window.Shopify?.currency?.active?.trim();
  if (shopify && isIso4217(shopify)) {
    return shopify.toUpperCase();
  }

  return null;
}

function formatCurrencyToggleFallback(iso: string): string {
  try {
    const parts = new Intl.NumberFormat(undefined, { style: "currency", currency: iso }).formatToParts(0);
    const sym = parts.find((p) => p.type === "currency")?.value ?? "";
    return sym ? `${iso} ${sym}` : iso;
  } catch {
    return iso;
  }
}

function syncDisclosureCountryBlock(disclosure: HTMLElement, activeCode: string): void {
  const label = disclosure.querySelector<HTMLElement>("[data-rebalance-currency-toggle-label]");
  if (label) {
    const match = disclosure.querySelector<HTMLElement>(
      `a[data-disclosure-option][data-currency-code="${activeCode}"]`
    );
    if (match) {
      const inner = match.querySelector("span") ?? match;
      const text = inner.textContent?.trim();
      if (text) {
        label.textContent = text;
      }
    } else {
      label.textContent = formatCurrencyToggleFallback(activeCode);
    }
  }

  const options = disclosure.querySelectorAll<HTMLAnchorElement>(
    "a[data-disclosure-option][data-currency-code]"
  );
  options.forEach((opt) => {
    const code = opt.dataset.currencyCode?.trim().toUpperCase();
    const isCurrent = code === activeCode;
    if (isCurrent) {
      opt.setAttribute("aria-current", "true");
    } else {
      opt.removeAttribute("aria-current");
    }
    const span = opt.querySelector("span");
    if (span) {
      span.classList.toggle("border-scheme-text", isCurrent);
      span.classList.toggle("border-b-text", isCurrent);
      span.classList.toggle("border-transparent", !isCurrent);
    }
  });
}

function applyCurrencyToAllDisclosures(activeCode: string): void {
  document.querySelectorAll<HTMLElement>("[data-disclosure-country]").forEach((disclosure) => {
    syncDisclosureCountryBlock(disclosure, activeCode);
  });
}

export function syncActiveCurrencyLabels(): void {
  const activeCode = getActiveCurrencyCodeSync();
  if (!activeCode) {
    return;
  }

  applyCurrencyToAllDisclosures(activeCode);
}

/**
 * Fetches cart JSON — cart.currency matches the session's presentment currency
 * even when Liquid and Shopify.currency.active lag (e.g. Canada primary market).
 */
export async function syncActiveCurrencyLabelsFromCart(): Promise<void> {
  try {
    const res = await fetch(getCartJsUrl(), { credentials: "same-origin" });
    if (!res.ok) {
      return;
    }

    const cart = (await res.json()) as { currency?: string };
    const raw = cart.currency?.trim();
    if (!raw || !isIso4217(raw)) {
      return;
    }

    const code = raw.toUpperCase();
    try {
      window.sessionStorage.setItem(STORAGE_KEY, code);
    } catch {
      // ignore
    }

    applyCurrencyToAllDisclosures(code);
  } catch {
    // Network / parse — sync pass already ran from session/URL/Shopify
  }
}

export function scheduleActiveCurrencyLabelSync(): void {
  const runSync = (): void => {
    syncActiveCurrencyLabels();
  };

  const runCart = (): void => {
    void syncActiveCurrencyLabelsFromCart();
  };

  runSync();
  requestAnimationFrame(() => {
    runSync();
    requestAnimationFrame(runSync);
  });
  window.setTimeout(runSync, 0);
  window.setTimeout(runSync, 50);
  window.setTimeout(runSync, 200);
  window.setTimeout(runSync, 600);
  window.setTimeout(runSync, 1500);

  runCart();
  window.setTimeout(runCart, 0);
  window.setTimeout(runCart, 100);
  window.setTimeout(runCart, 400);
  window.setTimeout(runCart, 1000);
  window.setTimeout(runCart, 2000);

  window.addEventListener(
    "load",
    () => {
      runSync();
      runCart();
    },
    { once: true }
  );
  document.addEventListener("shopify:section:load", () => {
    runSync();
    runCart();
  });
}
